import type { PresentationMode } from "./presentation";

interface SceneLike {
  config?: {
    scene_type?: string;
  } | null;
}

interface SceneIndices {
  quiz: number;
}

export const CLASSIC_GATED_SCENE_TYPES = [
  "actionable_content",
  "scenario",
  "code_review",
] as const;

export const CARD_ONLY_GATED_SCENE_TYPES = [
  "smishing_simulation",
  "vishing_simulation",
] as const;

export const GATED_SCENE_TYPES = [
  ...CLASSIC_GATED_SCENE_TYPES,
  ...CARD_ONLY_GATED_SCENE_TYPES,
] as const;

export type GatedSceneType = (typeof GATED_SCENE_TYPES)[number];

const CLASSIC_GATED_SCENE_TYPE_SET: ReadonlySet<string> = new Set(CLASSIC_GATED_SCENE_TYPES);
const CARD_ONLY_GATED_SCENE_TYPE_SET: ReadonlySet<string> = new Set(CARD_ONLY_GATED_SCENE_TYPES);
export function isGatedSceneType(
  value: string | undefined | null,
  presentationMode: PresentationMode = "classic"
): value is GatedSceneType {
  if (typeof value !== "string") return false;
  if (CARD_ONLY_GATED_SCENE_TYPE_SET.has(value)) {
    return presentationMode === "cards";
  }
  return CLASSIC_GATED_SCENE_TYPE_SET.has(value);
}

export type SceneCompletionState = Partial<Record<GatedSceneType, boolean>>;

interface CanProceedToNextSceneArgs {
  isPreviewMode: boolean;
  isEditMode: boolean;
  currentScene: number;
  scenes: SceneLike[];
  sceneIndices: SceneIndices;
  quizCompleted: boolean;
  completions: SceneCompletionState;
  presentationMode?: PresentationMode;
}

export function canProceedToNextScene({
  isPreviewMode,
  isEditMode,
  currentScene,
  scenes,
  sceneIndices,
  quizCompleted,
  completions,
  presentationMode = "classic",
}: CanProceedToNextSceneArgs): boolean {
  if (isPreviewMode || isEditMode) {
    return currentScene < scenes.length - 1;
  }

  if (currentScene === sceneIndices.quiz) {
    return quizCompleted;
  }

  const currentSceneConfig = scenes[currentScene]?.config;
  const sceneType = currentSceneConfig?.scene_type;

  if (isGatedSceneType(sceneType, presentationMode)) {
    return completions[sceneType] === true;
  }

  return currentScene < scenes.length - 1;
}
